import React, {useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {FormattedMessage, useIntl} from 'react-intl';

import {IStore} from 'redux/interface';
import {
  createJournal,
  getDialogues,
  getJournals,
  getTranslations,
  journalAsyncActions, removeJournal
} from 'redux/pageAsyncActions';
import {useGetTranslations} from 'utils/hooks/useGetTranslations';
import {TranslationKeys} from 'utils/constants/routeConstants';

import JournalImg from 'assets/image/menu-auth/jun_journal.png';
import {ButtonVariant, GritxButton} from '@wholesalechange/chatcomponent';
import NotFoundInfo from '../../components/not-found-info';
import CrisisTextLineImg from 'assets/image/library/crisis-text-line.jpeg';
import Loader from '../../components/loader';
import {useNavigate} from 'react-router-dom';
import {IJournalDialogue} from '../../redux/journal/interfaces';
import JournalsCardSection from '../../components/journals-card-section';
import {clearJournal} from '../../redux/journal/actions';
import {logoutAction} from '../../redux/auth/authAsyncActions';
import {IRadioInput} from '../../components/input-radio/interfaces';
import InputRadio from '../../components/input-radio';
import Stats from './stats';

import './styles.scss';

export const Journals: React.FunctionComponent = () => {
  const {
    journal: {
      journal,
      journals
    },
    status: {
      loading
    },
    auth: {
      relogin
    }
  } = useSelector((state: IStore) => state);
  const intl = useIntl();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [sortedJournals, setSortedJournals] = useState<IJournalDialogue[]>([]);
  const [sort, setSort] = useState<string>('new');
  const [afterCreate, setAfterCreate] = useState<boolean>(false);
  const shouldProcessRelogin = useRef(true);

  useEffect(() => {
    if (shouldProcessRelogin.current) {
      shouldProcessRelogin.current = false;
      if (relogin) {
        dispatch(logoutAction());
      }
    }
  }, [relogin]);

  useEffect(() => {
    dispatch(clearJournal());
  }, []);

  const sortRadioInputs: IRadioInput[] = [
    {
      id: 0,
      name: 'new',
      label: 'Newest',
      value: 'new'
    },
    {
      id: 1,
      name: 'old',
      label: 'Oldest',
      value: 'old'
    }];

  function getTime(date?: Date) {
    return date != null ? new Date(date).getTime() : 0;
  }

  function getSortedJournals(sortMethod: string) {
    const journalCopy = journals.slice(0);

    if (sortMethod === 'old') {
      return journalCopy.sort((a, b) => getTime(a.dialogUserDate) - getTime(b.dialogUserDate));
    }

    return journalCopy.sort((a, b) => getTime(b.dialogUserDate) - getTime(a.dialogUserDate));
  }

  useEffect(() => {
    if (journals) {
      setSortedJournals(getSortedJournals(sort));
    }
  }, [journals, sort]);

  useGetTranslations({
    translationKey: TranslationKeys.Journals
  });

  useEffect(() => {
    if (afterCreate && journal) {
      navigate(`/journal/${journal.dialogId}`);
      setAfterCreate(false);
    }
  }, [journal]);

  useEffect(() => {
    dispatch(journalAsyncActions[getJournals]());
  }, []);

  function handleOpenDialogue(journalDialogue: IJournalDialogue) {
    navigate(`/journal/${journalDialogue.dialogId}`);
  }

  function onCreateJournal() {
    const date = new Date();
    const dateStr = intl.formatDate(date, {year: 'numeric',
      month: 'long',
      hour: 'numeric',
      minute: 'numeric',
      day: '2-digit'});

    dispatch(journalAsyncActions[createJournal]({
      dialogId: 0,
      dialogName: `Journal of ${dateStr}`,
      dialogUserDate: date,
      percentage: 0
    }));
    setAfterCreate(true);
  }

  function handleChangeSort(value: string) {
    setSort(value);
  }

  function renderJournalCards() {
    return <div className="journal__cards">
      {sortedJournals.length === 0 ? <NotFoundInfo
        title={intl.formatMessage({
          id: 'gritx.journals.sectionCards.notFound',
          defaultMessage: 'Sorry, but nothing can be found.'
        })}
        image={CrisisTextLineImg}
      /> : <JournalsCardSection
        showLoader={loading.includes(getJournals)}
        cards={sortedJournals}
        onClickCard={handleOpenDialogue}
      />}
    </div>;
  }

  return <section className={'journals container'}>
    {
      !loading.includes(getTranslations) && <>
        <h1 className="journals__title">
          <img src={JournalImg} alt="" className="journals__icon"/>
          <FormattedMessage
            id={'gritx.journals.title'}
            defaultMessage={'journals'}
          />
        </h1>
        <Stats/>
        <div className="journals__header">
          <div className="journals__header-sort">
            <InputRadio inputs={sortRadioInputs} value={sort} onChange={handleChangeSort} label={intl.formatMessage({
              id: 'gritx.journals.sort.title',
              defaultMessage: 'Sort by:'
            })}/>
          </div>
          <GritxButton
            title={intl.formatMessage({
              id: 'gritx.journals.create',
              defaultMessage: 'Create'
            })}
            className={'journals__button'}
            variant={ButtonVariant.Primary}
            onClick={onCreateJournal}
          />
        </div>
        {
          loading.includes(getJournals) || loading.includes(removeJournal)
          || loading.includes(createJournal) ? <Loader/> : renderJournalCards()
        }
      </>
    }
  </section>;
};
